'use client'

// React Imports
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

// Dictionary Imports
import { dictionaries, resolvePersistedLang } from './dictionaries'
import type { Dictionary, Lang } from './dictionaries'

type LanguageContextValue = {
  lang: Lang
  setLang: (lang: Lang) => void
  dictionary: Dictionary
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

const STORAGE_KEY = 'imago-lang'

// The cookie mirrors the choice so the Next SERVER already renders the right language —
// no english flash before hydration. localStorage stays for the cross-tab sync.
export const LANG_COOKIE = 'imago-lang'

const FALLBACK_LANG: Lang = 'it-IT'

const persist = (next: Lang) => {
  localStorage.setItem(STORAGE_KEY, next)
  document.cookie = `${LANG_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`
}

export const LanguageProvider = ({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) => {
  // initialLang comes from the cookie read server-side (Providers): SSR and the first
  // client render agree on the right language.
  const [lang, setLangState] = useState<Lang>(initialLang ?? FALLBACK_LANG)

  useEffect(() => {
    const stored = resolvePersistedLang(localStorage.getItem(STORAGE_KEY))

    if (stored !== undefined) {
      setLangState(stored)
      persist(stored)
    }

    const onStorage = (event: StorageEvent) => {
      const written = event.key === STORAGE_KEY ? resolvePersistedLang(event.newValue) : undefined

      if (written !== undefined) {
        setLangState(written)
        document.cookie = `${LANG_COOKIE}=${written}; path=/; max-age=31536000; samesite=lax`
      }
    }

    window.addEventListener('storage', onStorage)

    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      setLang: (next: Lang) => {
        persist(next)
        setLangState(next)
      },
      dictionary: dictionaries[lang]
    }),
    [lang]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useTranslation = () => {
  const context = useContext(LanguageContext)

  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider')
  }

  const { dictionary } = context

  // Dotted-path lookup; a missing leaf returns the key itself so a partial dictionary
  // is always safe. Stable identity across renders (only changes with the language).
  const t = useCallback(
    (key: string): string => {
      const value = key.split('.').reduce<unknown>((node, part) => {
        return node !== null && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined
      }, dictionary)

      return typeof value === 'string' ? value : key
    },
    [dictionary]
  )

  return { t, lang: context.lang, setLang: context.setLang }
}
